/**
 * NextDeadlineStrip — Dashboard "next milestone" strip.
 *
 * PR-Cluster-1-c (docs/ux-design-plan.md §10) — closes the last
 * deferred item from Cluster 1 R2 Blocker BC1 ("earliest-deadline
 * hero"). The exec's scan-mode question after "are we on track"
 * (HeroKpi) is "and by when do we need to be there" — this strip
 * answers that in one row.
 *
 * Rules:
 *   - Self-hides when the workspace has no accreditation targets.
 *     A workspace that hasn't set any target dates doesn't have a
 *     "next milestone" to render — the strip would be a permanent
 *     empty state.
 *   - Ranks targets by target_date ASC, keeps up to 3 to keep the
 *     strip a single visual row. Overdue targets sort to the front
 *     (their days_to is negative).
 *   - Each pill mirrors CountdownBadge's tone rules (muted / warn /
 *     bad) so a target that's 22 days out reads the same colour
 *     here as it does next to the framework score card below.
 *
 * Sits BETWEEN the HeroKpi and the ReadinessTrendCard so the visual
 * flow is: current status → next milestone → trend of getting there.
 */

import type { AccreditationTarget } from '../../lib/api';

function daysBetween(a: Date, b: Date): number {
  const oneDay = 24 * 60 * 60 * 1000;
  const aUtc = Date.UTC(a.getUTCFullYear(), a.getUTCMonth(), a.getUTCDate());
  const bUtc = Date.UTC(b.getUTCFullYear(), b.getUTCMonth(), b.getUTCDate());
  return Math.round((bUtc - aUtc) / oneDay);
}

type Tone = 'muted' | 'warn' | 'bad';

function toneFor(days: number): Tone {
  if (days < 0) return 'bad';
  if (days <= 30) return 'bad';
  if (days <= 60) return 'warn';
  return 'muted';
}

const TONE_PALETTE: Record<Tone, { bg: string; fg: string; border: string }> = {
  muted: {
    bg: 'var(--surface-alt)',
    fg: 'var(--text-muted)',
    border: 'var(--border)',
  },
  warn: {
    bg: 'var(--warning-bg)',
    fg: 'var(--warning-fg)',
    border: 'var(--band-amber)',
  },
  bad: {
    bg: 'var(--danger-bg)',
    fg: 'var(--danger-fg)',
    border: 'var(--band-red)',
  },
};

const MAX_VISIBLE = 3;

export function NextDeadlineStrip({
  targets,
  frameworkNames,
}: {
  targets: AccreditationTarget[];
  /**
   * Map of framework_id → display name so the strip can render
   * "SOC 2 + AI" instead of a `fw-soc2-ai` id. Sourced from the
   * assessment's framework_scores which the Dashboard already loads.
   */
  frameworkNames: Map<string, string>;
}) {
  if (targets.length === 0) return null;
  const now = new Date();
  const enriched = targets
    .map((t) => {
      const targetDate = new Date(`${t.target_date}T00:00:00Z`);
      if (Number.isNaN(targetDate.getTime())) return null;
      const days = daysBetween(now, targetDate);
      return { target: t, days };
    })
    .filter((r): r is { target: AccreditationTarget; days: number } => r !== null)
    .sort((a, b) => a.days - b.days)
    .slice(0, MAX_VISIBLE);
  if (enriched.length === 0) return null;
  const hiddenCount = Math.max(0, targets.length - enriched.length);

  return (
    <section
      aria-labelledby="next-deadline-heading"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-3)',
        padding: 'var(--space-3) var(--space-4)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-card)',
        background: 'var(--surface)',
        flexWrap: 'wrap',
      }}
    >
      <span
        id="next-deadline-heading"
        style={{
          fontSize: 'var(--font-size-3xs)',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: 'var(--text-dim)',
          fontWeight: 700,
          whiteSpace: 'nowrap',
        }}
      >
        Next milestones
      </span>
      <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap', flex: 1 }}>
        {enriched.map(({ target, days }) => (
          <DeadlinePill
            key={target.target_id}
            days={days}
            targetDate={target.target_date}
            mode={target.mode}
            frameworkLabel={
              frameworkNames.get(target.framework_id) ?? target.framework_id
            }
            notes={target.notes}
          />
        ))}
      </div>
      {hiddenCount > 0 ? (
        <span
          style={{
            fontSize: 'var(--font-size-3xs)',
            color: 'var(--text-dim)',
            fontFamily: 'var(--font-mono)',
            whiteSpace: 'nowrap',
          }}
        >
          +{hiddenCount} more
        </span>
      ) : null}
    </section>
  );
}

function DeadlinePill({
  days,
  targetDate,
  mode,
  frameworkLabel,
  notes,
}: {
  days: number;
  targetDate: string;
  mode: 'strict' | 'soft';
  frameworkLabel: string;
  notes: string | null;
}) {
  const tone = toneFor(days);
  const palette = TONE_PALETTE[tone];
  const isPast = days < 0;
  const countdown = isPast
    ? `Overdue ${Math.abs(days)}d`
    : days === 0
      ? 'Due today'
      : `${days}d`;
  const title = `${frameworkLabel} · target ${targetDate} (${mode})${notes ? ` — ${notes}` : ''}`;
  return (
    <span
      title={title}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        padding: '4px 10px',
        borderRadius: '999px',
        background: palette.bg,
        color: palette.fg,
        border: `1px solid ${palette.border}`,
        fontSize: 'var(--font-size-3xs)',
        fontWeight: 600,
        whiteSpace: 'nowrap',
      }}
    >
      <span>{frameworkLabel}</span>
      <span
        aria-hidden
        style={{ color: palette.border, fontFamily: 'var(--font-mono)', opacity: 0.6 }}
      >
        ·
      </span>
      <span style={{ fontFamily: 'var(--font-mono)' }}>{countdown}</span>
      {mode === 'soft' ? (
        <span style={{ opacity: 0.6, fontFamily: 'var(--font-mono)', fontWeight: 400 }}>
          soft
        </span>
      ) : null}
    </span>
  );
}
